import { useCallback, useMemo } from 'react';
import { fetchJson } from '@helpCenter/util/fetch/fetch';
import { doc, p } from '@atlaskit/adf-utils/builders';
import { getCloudId } from '@atlassian/help-center-common-util/meta';
import type { AgentMessage, ConversationProvider, HelpseekerMessage } from '../../common/types';
import type { CreateVirtualAgentConversationResponse, VirtualAgentConversationMessageResponse } from './types';

export const useVirtualAgentConversationProvider = ({
    projectId,
    isTestConversation,
}: {
    projectId: number | undefined;
    isTestConversation: boolean | undefined;
}): ConversationProvider => {
    const startConversation = useCallback(async (): Promise<CreateVirtualAgentConversationResponse> => {
        if (typeof projectId !== 'number') {
            throw new Error('Conversation provider does not support global scope');
        }

        const cloudId = getCloudId();

        const response = await fetchJson<CreateVirtualAgentConversationResponse>(
            `/gateway/api/jsm/virtual-agent/cloudId/${cloudId}/public/api/v1/web/conversation`,
            {
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    jiraProjectId: projectId,
                    createdAt: new Date().toISOString(),
                    isTestConversation: isTestConversation === true,
                }),
                method: 'POST',
            }
        );
        return response;
    }, [projectId, isTestConversation]);

    const sendMessage = useCallback(
        async (message: HelpseekerMessage): Promise<AgentMessage[]> => {
            if (typeof projectId !== 'number') {
                throw new Error('Conversation provider does not support global scope');
            }

            const cloudId = getCloudId();

            const response = await fetchJson<VirtualAgentConversationMessageResponse[]>(
                `/gateway/api/jsm/virtual-agent/cloudId/${cloudId}/public/api/v1/web/conversation/message`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        message: {
                            content: JSON.stringify(doc(p(message.message.content))),
                            optionId: message.message.optionId,
                            jiraField: message.message.jiraField,
                        },
                        timeCreated: message.timeCreated,
                        conversationChannelId: message.conversationChannelId,
                    }),
                    method: 'POST',
                }
            );

            return response;
        },
        [projectId]
    );

    return useMemo(() => ({ startConversation, sendMessage }), [sendMessage, startConversation]);
};
